import {
  API_HEADER,
  CountryCode, Gender,
} from "../utils/Constants";

import { apiRequest } from "./apiService";

interface BasePayload {
  countryCode: string;
  phoneNumber: string;
}

interface SendOtpPayload extends BasePayload {
  languageCode?: number;
}

interface VerifySMSPayload extends BasePayload {
  smsCode: string;
}

interface LogoutPayload {
  loginToken: string;
  serviceToken: string;
}

interface RegisterPayload {
  firstName: string;
  lastName: string;
  email: string;
  gender: Gender;
  registerToken: string;
}

interface VerifyLoginResult {
  loginExpireAt: number;
  nLoginToken: string;
  nRefreshToken: string;
  refreshExpireAt: number;
  serviceToken: string;
}

interface VerifyRegisterResult {
  registerToken: string;
}
/**
 * Function to validate phone number based on country code
 */
export const phoneValidate = (payload: BasePayload): boolean => {
  const trimmedPhoneNumber = payload.phoneNumber.trim();

  switch (payload.countryCode) {
    case CountryCode.HONG_KONG:
    case CountryCode.MACAU:
      return trimmedPhoneNumber.length === 8 && !isNaN(Number(trimmedPhoneNumber));
    case CountryCode.MAINLAND_CHINA:
      return trimmedPhoneNumber.length === 11 && !isNaN(Number(trimmedPhoneNumber));
    default:
      return false;
  }
};

interface AuthTokenResponse {
  serviceToken: string;
}

/**
 * Fetches service token after successful authentication
 */
export const getServiceToken = async (authToken: string): Promise<string> => {
  const result = await apiRequest<AuthTokenResponse>(
    `${API_HEADER}/login/auth`,
    {
      method: "GET",
      headers: {
        "Authorization": authToken,
      },
    },
    "Failed to retrieve service token"
  );

  return result.serviceToken;
};

/**
 * Verifies SMS code for registration
 */
export const verifyRegisterCode = async (payload: VerifySMSPayload): Promise<VerifyRegisterResult> => {
  const { countryCode, phoneNumber, smsCode } = payload;
  const url = `${API_HEADER}/verify/sms/register`;

  const requestBody = {
    countryCode,
    mobile: phoneNumber,
    code: smsCode,
  };

  const result = await apiRequest<VerifyRegisterResult>(
    url,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    },
    "SMS verification failed"
  );

  // For registration mode, return result is only registerToken
  return result as VerifyRegisterResult;
}

/**
 * Verifies SMS code for login
 * @param payload
 */
export const verifyCode = async (payload: VerifySMSPayload): Promise<VerifyLoginResult> => {
  const { countryCode, phoneNumber, smsCode } = payload;
  const url = `${API_HEADER}/verify/sms/login`;

  const requestBody = {
    countryCode,
    mobile: phoneNumber,
    code: smsCode,
  };

  const result = await apiRequest<Omit<VerifyLoginResult, "serviceToken">>(
    url,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    },
    "SMS verification failed"
  );

  // For login mode, fetch service token and merge with result
  const serviceToken = await getServiceToken(result.nLoginToken);
  return { ...result, serviceToken } as VerifyLoginResult;
};

/**
 * Sends SMS OTP for verification
 */
export const sendSMS = async (payload: SendOtpPayload): Promise<boolean> => {
  const { countryCode, phoneNumber, languageCode = 0 } = payload;

  try {
    await apiRequest<any>(
      `${API_HEADER}/verify/send/sms?countryCode=${countryCode}&mobile=${phoneNumber}&languageCode=${languageCode}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      "Failed to send OTP"
    );

    return true;
  } catch (error) {
    console.error("Failed to send OTP:", error instanceof Error ? error.message : String(error));
    return false;
  }
};

/**
 * Registers a new user
 */
export const register = async (payload: RegisterPayload): Promise<VerifyLoginResult> => {
  const { firstName, lastName, email, gender, registerToken } = payload;

  const requestBody = {
    firstName,
    lastName,
    email,
    gender,
  };

  const result = await apiRequest<Omit<VerifyLoginResult, "serviceToken">>(
    `${API_HEADER}/login/register`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Csrf-Token": registerToken,
      },
      body: JSON.stringify(requestBody),
    },
    "Registration failed"
  );

  const serviceToken = await getServiceToken(result.nLoginToken);
  return { ...result, serviceToken };
};

/**
 * Logs out user by invalidating tokens
 */
export const logout = async (payload: LogoutPayload): Promise<boolean> => {
  const { loginToken, serviceToken } = payload;

  try {
    const response = await fetch(
      `${API_HEADER}/login/logout`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": loginToken,
          "X-Csrf-Token": serviceToken,
        },
      }
    );

    return response.ok;
  } catch (error) {
    console.error("Logout failed:", error instanceof Error ? error.message : String(error));
    return false;
  }
};

export const refreshToken = async (authToken: string): Promise<VerifyLoginResult> => {
  const result = await apiRequest<VerifyLoginResult>(
    `${API_HEADER}/login/auth/refresh`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": authToken,
      },
    },
    "Failed to refresh token"
  );
  const serviceToken = await getServiceToken(result.nLoginToken);
  return { ...result, serviceToken };
}